import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Cloud } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import { IcCheck, IcChevronLeft, IcChevronRight, IcClock } from "@app/components/icons";
import { SpecText, StatusPill, btn, btnGhost, btnPrimary, btnSoft } from "@app/components/ui";
import { TONE_TAG } from "../gpu-hub/category-tone";

const ALL = "__all__";

/** Giờ mỗi session; 0 = Không giới hạn (không đặt, hoặc dài hơn tổng giờ chơi nên không bao giờ chạm tới, như bản thiết kế) */
export const sessionHours = (c: Cloud) =>
  c.max_hours_each_session && c.max_hours_each_session <= c.hours_of_use ? c.max_hours_each_session : 0;

/**
 * Gói có hàng chờ: admin ghi "Có" hoặc "Có hàng chờ" ở trường message_wait (không phân biệt hoa thường,
 * gõ có dấu hay không dấu đều được). Để trống, "Không" hoặc "Không hàng chờ" là không hàng chờ.
 */
export const hasQueue = (c: Cloud) => {
  const text = (c.message_wait || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[đĐ]/g, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (!text || /\bkhong\b/.test(text)) return false;

  return text === "co" || text.includes("co hang cho");
};

/** Nhãn hàng chờ như bản thiết kế: vàng = Có hàng chờ, xanh = Không hàng chờ */
export function QueueTag({ cloud }: { cloud: Cloud }) {
  const t = useT();
  const yes = hasQueue(cloud);

  return (
    <span
      className={clsx(
        "w-fit whitespace-nowrap rounded-md px-[7px] py-px text-[11px] font-semibold",
        yes ? "bg-warn-soft text-warn" : "bg-ok-soft text-ok"
      )}
    >
      {yes ? t("Có hàng chờ") : t("Không hàng chờ")}
    </span>
  );
}

/** Màu chữ nhóm gói: t-cloud (brand) rồi các màu danh mục t-gm, t-rd, t-ai, t-dh, t-gl */
const GROUP_TAGS = ["bg-brand-soft text-brand", TONE_TAG.gm, TONE_TAG.rd, TONE_TAG.ai, TONE_TAG.dh, TONE_TAG.gl];

interface CloudCatalogProps {
  clouds: Cloud[];
  /** discount_cloud của tài khoản */
  discount: number;
  selectedId: string;
  onPick: (cloud: Cloud) => void;
}

/** Danh sách gói Cloud: xem dạng thẻ hoặc bảng so sánh, lọc theo group như CloudCreatePage cũ. */
function CloudCatalog({ clouds, discount, selectedId, onPick }: CloudCatalogProps) {
  const t = useT();
  const [group, setGroup] = useState(ALL);
  const [view, setView] = useState<"cards" | "table">("cards");
  const [ready, setReady] = useState(false);
  const [edges, setEdges] = useState({ start: true, end: true });

  const gridRef = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => Array.from(new Set(clouds.map((c) => c.group).filter(Boolean))), [clouds]);
  // Mỗi nhóm gói 1 màu như bản thiết kế (nhóm đầu màu brand, các nhóm sau lần lượt theo màu danh mục)
  const groupTag = (group: string) => GROUP_TAGS[Math.max(0, groups.indexOf(group)) % GROUP_TAGS.length];
  const list = clouds.filter((c) => (group === ALL || c.group === group) && (!ready || c.cloudReady));

  const price = (c: Cloud) => c.price - (c.price * discount) / 100;
  // Giá mỗi giờ chơi, làm tròn 100đ
  const perHour = (c: Cloud) => (c.hours_of_use ? Math.round(price(c) / c.hours_of_use / 100) * 100 : 0);
  const bestPerHour = Math.min(...list.map(perHour).filter(Boolean));
  const session = (c: Cloud) => (sessionHours(c) ? `${sessionHours(c)} ${t("giờ")}` : t("Không giới hạn"));

  const updateEdges = () => {
    const g = gridRef.current;

    if (g) setEdges({ start: g.scrollLeft < 4, end: g.scrollLeft + g.clientWidth >= g.scrollWidth - 4 });
  };

  useEffect(() => {
    gridRef.current?.scrollTo({ left: 0 });
    updateEdges();
  }, [group, ready, view, clouds.length]);

  useEffect(() => {
    window.addEventListener("resize", updateEdges);

    return () => window.removeEventListener("resize", updateEdges);
  }, []);

  const slide = (dir: number) => gridRef.current?.scrollBy({ left: dir * gridRef.current.clientWidth, behavior: "smooth" });

  const chip = (active: boolean) =>
    clsx(
      "inline-flex flex-none items-baseline gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium leading-5",
      active ? "border-fg bg-fg text-surface" : "border-line bg-surface text-muted hover:text-fg"
    );

  const status = (c: Cloud) =>
    c.cloudReady ? <StatusPill tone="ok">{t("Còn máy")}</StatusPill> : <StatusPill tone="bad">{t("Hết máy")}</StatusPill>;

  const pickButton = (c: Cloud) => {
    const on = selectedId === c._id;

    if (!c.cloudReady) {
      return (
        <button type="button" disabled className={clsx(btn, btnGhost, "w-full !opacity-100")}>
          <IcClock className="h-4 w-4" />
          {t("Tạm hết máy")}
        </button>
      );
    }

    return (
      <button type="button" className={clsx(btn, "w-full", on ? btnSoft : btnPrimary)} onClick={() => onPick(c)}>
        {on && <IcCheck className="h-4 w-4" />}
        {on ? t("Đã chọn") : t("Chọn gói")}
      </button>
    );
  };

  const arrow =
    "absolute top-1/2 z-[3] grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface text-brand shadow-card hover:bg-brand hover:text-white";

  return (
    <section className="flex min-w-0 flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          <button type="button" className={chip(group === ALL)} onClick={() => setGroup(ALL)}>
            {t("Tất cả")} <em className="text-xs not-italic opacity-70">{clouds.length}</em>
          </button>
          {groups.map((g) => (
            <button key={g} type="button" className={chip(group === g)} onClick={() => setGroup(g)}>
              {g} <em className="text-xs not-italic opacity-70">{clouds.filter((c) => c.group === g).length}</em>
            </button>
          ))}
        </div>
        <div className="rtabs ml-auto inline-flex gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
          {(
            [
              ["cards", "Thẻ"],
              ["table", "So sánh"],
            ] as [typeof view, string][]
          ).map(([key, text]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={clsx(
                "rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                view === key ? "bg-surface text-fg shadow-card" : "text-muted"
              )}
            >
              {t(text)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2.5 text-[13px] text-muted">
        <span>
          {t("Có")} <b className="text-fg">{list.length}</b> {t("gói")}
        </span>
        <label className="inline-flex cursor-pointer select-none items-center gap-2">
          <input type="checkbox" className="peer sr-only" checked={ready} onChange={(e) => setReady(e.target.checked)} />
          <span className="relative h-5 w-[34px] rounded-[10px] bg-line-2 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:bg-brand peer-checked:after:translate-x-3.5" />
          {t("Chỉ hiện gói còn máy")}
        </label>
      </div>

      {!list.length ? (
        <p className="m-0 rounded-card border border-dashed border-line-2 px-4 py-10 text-center text-muted">
          {t("Hiện không có gói nào còn máy.")}
        </p>
      ) : view === "table" ? (
        /* ---------- bảng so sánh ---------- */
        <div className="overflow-x-auto rounded-card border border-line bg-surface">
          <table className="w-full min-w-[720px] border-collapse text-[13px]">
            <thead>
              <tr>
                <th className="sticky left-0 bg-surface-2 px-3 py-2.5" />
                {list.map((c) => (
                  <th
                    key={c._id}
                    className={clsx(
                      "px-3 py-2.5 text-left font-bold",
                      selectedId === c._id ? "bg-brand-soft text-brand" : "bg-surface-2 text-fg"
                    )}
                  >
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ["Giá (đã gồm VAT)", (c: Cloud) => <b className="tabular-nums">{formatMoneyVN(price(c))} VND</b>],
                  [
                    "Giá mỗi giờ chơi",
                    (c: Cloud) => (
                      <span className={clsx("tabular-nums", perHour(c) === bestPerHour && "font-bold text-ok")}>
                        ≈ {formatMoneyVN(perHour(c))}đ
                      </span>
                    ),
                  ],
                  ["Giờ chơi", (c: Cloud) => `${c.hours_of_use} ${t("giờ")}`],
                  ["Hạn sử dụng", (c: Cloud) => `${c.days_of_use * 24} ${t("giờ")}`],
                  ["Mỗi session (tự tắt sau)", session],
                  ["Hàng chờ", (c: Cloud) => <QueueTag cloud={c} />],
                  ["Tình trạng", status],
                  ["CPU", (c: Cloud) => <SpecText value={c.cpu} />],
                  ["RAM", (c: Cloud) => <SpecText value={c.ram} />],
                  ["SSD", (c: Cloud) => <SpecText value={c.ssd} />],
                  [
                    "VGA",
                    (c: Cloud) => (
                      <span className="font-bold text-ok">
                        <SpecText value={c.vga} />
                      </span>
                    ),
                  ],
                  ["Hệ điều hành", (c: Cloud) => <SpecText value={c.os} />],
                  ["", pickButton],
                ] as [string, (c: Cloud) => React.ReactNode][]
              ).map(([label, cell]) => (
                <tr key={label}>
                  <th className="sticky left-0 z-[1] whitespace-nowrap border-t border-line bg-surface px-3 py-2.5 text-left text-[12.5px] font-medium text-muted">
                    {t(label)}
                  </th>
                  {list.map((c) => (
                    <td
                      key={c._id}
                      className={clsx(
                        "min-w-[128px] border-t border-line px-3 py-2.5",
                        selectedId === c._id && "bg-brand-soft"
                      )}
                    >
                      {cell(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ---------- dạng thẻ, 1 hàng có mũi tên ---------- */
        <div className="pk-wrap relative flex flex-1 flex-col">
          {!edges.start && (
            <button type="button" className={clsx(arrow, "-left-5 max-[760px]:-left-3")} onClick={() => slide(-1)}>
              <IcChevronLeft className="h-[18px] w-[18px]" />
            </button>
          )}
          <div
            ref={gridRef}
            onScroll={updateEdges}
            className="no-scrollbar -mx-[3px] -mb-2 -mt-1 grid flex-1 snap-x snap-mandatory grid-flow-col gap-3 overflow-x-auto px-[3px] pb-2 pt-1 pk-grid"
          >
            {list.map((c) => {
              const on = selectedId === c._id;

              return (
                <article
                  key={c._id}
                  onClick={() => c.cloudReady && onPick(c)}
                  className={clsx(
                    "flex min-w-0 snap-start flex-col gap-2.5 rounded-card border bg-surface p-3.5 transition-shadow",
                    c.cloudReady ? "cursor-pointer hover:shadow-card" : "cursor-default",
                    on ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line"
                  )}
                >
                  {/* nhóm + hàng chờ cạnh nhau như bản thiết kế */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {c.group && (
                      <span className={clsx("w-fit rounded-md px-[7px] py-px text-[11px] font-semibold", groupTag(c.group))}>
                        {c.group}
                      </span>
                    )}
                    <QueueTag cloud={c} />
                  </div>
                  <h3 className="flex items-center gap-2 text-base font-bold">
                    {c.name}
                    <span className="ml-auto">{status(c)}</span>
                    {on && (
                      <span className="grid h-[22px] w-[22px] flex-none place-items-center rounded-full bg-brand text-white">
                        <IcCheck className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </h3>
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <strong className={clsx("text-[22px] font-bold tabular-nums", c.cloudReady ? "text-brand" : "text-muted")}>
                      {formatMoneyVN(price(c))}
                    </strong>
                    <span className="text-xs text-muted">VND · {t("đã gồm VAT")}</span>
                    {discount > 0 && <s className="text-xs text-faint">{formatMoneyVN(c.price)}</s>}
                  </div>
                  <div className="-mt-1.5 text-xs text-muted">
                    ≈ <b className="text-ok">{formatMoneyVN(perHour(c))}đ</b> {t("mỗi giờ chơi")}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(
                      [
                        [`${c.hours_of_use}h`, "Giờ chơi"],
                        [`${c.days_of_use * 24}h`, "Hạn sử dụng"],
                        [sessionHours(c) ? `${sessionHours(c)}h` : "∞", "Mỗi session"],
                      ] as [string, string][]
                    ).map(([value, text]) => (
                      <div key={text} className="min-w-0 rounded-[9px] bg-surface-2 px-2 py-1.5 leading-tight">
                        <b className="block whitespace-nowrap text-sm tabular-nums">{value}</b>
                        <span className="whitespace-nowrap text-[11px] text-muted">{t(text)}</span>
                      </div>
                    ))}
                  </div>
                  <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 text-[12.5px]">
                    {(
                      [
                        ["CPU", c.cpu],
                        ["RAM", c.ram],
                        ["SSD", c.ssd],
                        ["VGA", c.vga],
                        ["OS", c.os],
                      ] as [string, string][]
                    ).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="font-medium text-faint">{k}</dt>
                        <dd className={clsx("m-0 min-w-0 font-medium", k === "VGA" && "font-bold text-ok")}>
                          <SpecText value={v} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-auto" onClick={(e) => e.stopPropagation()}>
                    {pickButton(c)}
                  </div>
                  {!c.cloudReady && (
                    <p className="m-0 -mt-1 text-center text-xs text-muted">{t("Hết máy, chờ có máy mới tạo được gói")}</p>
                  )}
                </article>
              );
            })}
          </div>
          {!edges.end && (
            <button type="button" className={clsx(arrow, "-right-5 max-[760px]:-right-3")} onClick={() => slide(1)}>
              <IcChevronRight className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default CloudCatalog;
